import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import {
  DEMO_COHORT,
  DEMO_INSTRUCTOR,
  DEMO_PROGRESS,
  DEMO_STUDENT,
  DEMO_STUDENT_PROGRESS,
  DEMO_STUDENTS,
  INITIAL_CURRICULUM,
} from "../data/seed";
import { calculateReward, isRegionUnlocked } from "../lib/progression";
import { demoMode, supabase } from "../lib/supabase";
import { generatePuzzleInstance, validatePuzzleSubmission } from "../puzzles/core/registry";
import { createTimeSeed } from "../puzzles/core/vectorDrone";
import type {
  Attempt,
  Cohort,
  Curriculum,
  GuildId,
  Quest,
  Region,
  RiskRules,
  StudentProgress,
  ToastMessage,
  UserProfile,
} from "../types/domain";

interface StartAttemptResult {
  attempt: Attempt;
  instance: unknown;
}

interface SubmitAttemptResult {
  attempt: Attempt;
  success: boolean;
  feedback: string;
  score: number;
}

interface PortalContextValue {
  user: UserProfile | null;
  authLoading: boolean;
  isDemo: boolean;
  curriculum: Curriculum;
  cohort: Cohort;
  progress: StudentProgress;
  students: UserProfile[];
  studentProgress: Record<string, StudentProgress>;
  toasts: ToastMessage[];
  signInWithGoogle(): Promise<void>;
  signInWithMagicLink(email: string): Promise<string>;
  signInDemo(role: "student" | "instructor"): void;
  signOut(): Promise<void>;
  switchGuild(guild: GuildId): Promise<void>;
  updateProfile(patch: Partial<UserProfile>): Promise<void>;
  startAttempt(quest: Quest): Promise<StartAttemptResult>;
  submitAttempt(
    attempt: Attempt,
    instance: unknown,
    quest: Quest,
    submission: Record<string, unknown>,
    reflection: string,
  ): Promise<SubmitAttemptResult>;
  saveCurriculum(next: Curriculum): Promise<void>;
  publishCurriculum(next?: Curriculum): Promise<void>;
  addRoster(entries: Array<{ email: string; fullName: string }>): Promise<number>;
  updateRegionUnlocks(
    regionId: string,
    settings: Pick<
      Region,
      | "unlockAvailableFrom"
      | "unlockCompletionThreshold"
      | "unlockPrerequisiteRegionIds"
    >,
  ): Promise<void>;
  updateRiskRules(rules: RiskRules): void;
  notify(title: string, detail: string, tone?: ToastMessage["tone"]): void;
  dismissToast(id: string): void;
}

const PortalContext = createContext<PortalContextValue | null>(null);

const STORAGE_KEY = "mathportal-demo-state-v1";

interface PersistedState {
  user: UserProfile | null;
  curriculum: Curriculum;
  cohort: Cohort;
  students: UserProfile[];
  studentProgress: Record<string, StudentProgress>;
}

function loadDemoState(): PersistedState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as PersistedState;
  } catch {
    // Invalid local demo data should never prevent the portal from loading.
  }
  return {
    user: null,
    curriculum: INITIAL_CURRICULUM,
    cohort: DEMO_COHORT,
    students: DEMO_STUDENTS,
    studentProgress: DEMO_STUDENT_PROGRESS,
  };
}

function mapProfile(row: Record<string, unknown>): UserProfile {
  return {
    id: String(row.id),
    fullName: String(row.full_name ?? "Math Adventurer"),
    email: String(row.email ?? ""),
    role: (row.role as UserProfile["role"]) ?? "student",
    avatarUrl: row.avatar_url ? String(row.avatar_url) : undefined,
    guild: (row.guild as GuildId) ?? "explorer",
    level: Number(row.level ?? 1),
    xp: Number(row.xp ?? 0),
    xpNext: Number(row.xp_next ?? 500),
    guildReputation:
      (row.guild_reputation as UserProfile["guildReputation"]) ??
      DEMO_STUDENT.guildReputation,
    lastActiveAt: String(row.last_active_at ?? new Date().toISOString()),
  };
}

interface BootstrapPayload {
  curriculum?: Curriculum;
  cohort?: Cohort;
  user?: UserProfile;
  students?: UserProfile[];
  studentProgress?: Record<string, StudentProgress>;
}

async function getFunctionErrorMessage(
  error: unknown,
  fallback: string,
): Promise<string> {
  const context = (error as { context?: unknown } | null)?.context;
  const response = context as {
    clone?: () => Response;
    json?: () => Promise<unknown>;
    text?: () => Promise<string>;
  };
  if (response?.json) {
    try {
      const jsonReadable = response.clone ? response.clone() : response;
      const body = await jsonReadable.json?.();
      if (body && typeof body === "object") {
        const message =
          (body as { error?: unknown; message?: unknown }).error ??
          (body as { message?: unknown }).message;
        if (typeof message === "string" && message.trim()) return message;
      }
    } catch {
      // Fall through to the Supabase client error message.
    }
  }
  if (response?.text) {
    try {
      const textReadable = response.clone ? response.clone() : response;
      const body = await textReadable.text?.();
      if (body?.trim()) return body;
    } catch {
      // Fall through to the Supabase client error message.
    }
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
}

export function PortalProvider({ children }: PropsWithChildren) {
  const initial = useMemo(loadDemoState, []);
  const [user, setUser] = useState<UserProfile | null>(initial.user);
  const [authLoading, setAuthLoading] = useState(!demoMode);
  const [curriculum, setCurriculum] = useState(initial.curriculum);
  const [cohort, setCohort] = useState(initial.cohort);
  const [students, setStudents] = useState(initial.students);
  const [studentProgress, setStudentProgress] = useState(initial.studentProgress);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const notify = useCallback(
    (
      title: string,
      detail: string,
      tone: ToastMessage["tone"] = "success",
    ) => {
      const id = crypto.randomUUID();
      setToasts((current) => [...current, { id, title, detail, tone }]);
      window.setTimeout(
        () => setToasts((current) => current.filter((toast) => toast.id !== id)),
        5200,
      );
    },
    [],
  );

  useEffect(() => {
    if (!demoMode) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ user, curriculum, cohort, students, studentProgress }),
    );
  }, [user, curriculum, cohort, students, studentProgress]);

  useEffect(() => {
    if (demoMode || !supabase) {
      setAuthLoading(false);
      return;
    }

    const client = supabase;
    let mounted = true;
    const loadUser = async () => {
      const { data } = await client.auth.getUser();
      if (!data.user || !mounted) {
        setUser(null);
        setAuthLoading(false);
        return;
      }
      const { data: profile } = await client
        .from("profiles")
        .select("*")
        .eq("id", data.user.id)
        .single();
      if (profile && mounted) {
        const { data: roles } = await client
          .from("user_roles")
          .select("role")
          .eq("user_id", data.user.id)
          .order("role", { ascending: false })
          .limit(1);
        const mapped = mapProfile({
          ...profile,
          email: data.user.email,
          role: roles?.[0]?.role ?? "student",
        });
        setUser(mapped);
        const { data: bootstrap } = await client.functions.invoke<BootstrapPayload>(
          "portal-bootstrap",
        );
        if (bootstrap && mounted) {
          if (bootstrap.user) setUser(bootstrap.user);
          if (bootstrap.curriculum) setCurriculum(bootstrap.curriculum);
          if (bootstrap.cohort) setCohort(bootstrap.cohort);
          if (bootstrap.students) setStudents(bootstrap.students);
          if (bootstrap.studentProgress) {
            setStudentProgress(bootstrap.studentProgress);
          }
        }
      }
      setAuthLoading(false);
    };
    void loadUser();
    const { data: listener } = client.auth.onAuthStateChange(() => {
      void loadUser();
    });
    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const signInWithMagicLink = async (email: string) => {
    if (!supabase) {
      return "Supabase is not configured. Use one of the demo roles below.";
    }
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        shouldCreateUser: false,
      },
    });
    if (error) throw error;
    return "Check your email for a secure sign-in link.";
  };

  const signInWithGoogle = async () => {
    if (!supabase) {
      throw new Error("Supabase is not configured. Use one of the demo roles below.");
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const signInDemo = (role: "student" | "instructor") => {
    const next = role === "student" ? DEMO_STUDENT : DEMO_INSTRUCTOR;
    setUser(next);
    notify(
      `Welcome, ${next.fullName}`,
      role === "student"
        ? "Your adventure map is ready."
        : "The faculty command center is ready.",
    );
  };

  const signOut = async () => {
    if (!demoMode && supabase) await supabase.auth.signOut();
    setUser(null);
  };

  const updateProfile = async (patch: Partial<UserProfile>) => {
    if (!user) return;
    const next = { ...user, ...patch };
    setUser(next);
    setStudents((current) =>
      current.map((student) => (student.id === user.id ? next : student)),
    );
    if (!demoMode && supabase) {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: next.fullName,
          avatar_url: next.avatarUrl,
          guild: next.guild,
        })
        .eq("id", user.id);
      if (error) throw error;
    }
  };

  const switchGuild = async (guild: GuildId) => {
    await updateProfile({ guild });
    notify(
      "Guild path changed",
      "Your mastery remains shared while the map reveals a new recommended route.",
      "info",
    );
  };

  const startAttempt = async (quest: Quest): Promise<StartAttemptResult> => {
    if (!user) throw new Error("Sign in before starting a quest.");
    if (quest.templateId === "coming-soon") {
      throw new Error("This expedition is still being built.");
    }
    const region = curriculum.regions.find(
      (candidate) => candidate.id === quest.regionId,
    );
    const current = studentProgress[user.id] ?? DEMO_PROGRESS;
    if (region && !isRegionUnlocked(region, current)) {
      throw new Error("This region is locked for your cohort.");
    }
    if (!demoMode && supabase) {
      const { data, error } = await supabase.functions.invoke("start-attempt", {
        body: { questId: quest.id },
      });
      if (error) {
        throw new Error(
          await getFunctionErrorMessage(error, "Unable to start puzzle attempt."),
        );
      }
      return data as StartAttemptResult;
    }

    const questAttempts = current.attempts.filter(
      (attempt) => attempt.questId === quest.id,
    );
    const active = questAttempts.find((attempt) => attempt.status === "active");
    if (active) {
      return {
        attempt: active,
        instance: generatePuzzleInstance(quest.templateId, quest.config, active.seed),
      };
    }
    if (quest.maxAttempts && questAttempts.length >= quest.maxAttempts) {
      throw new Error("The maximum number of attempts has been reached.");
    }
    const previous = [...questAttempts].sort((a, b) =>
      b.startedAt.localeCompare(a.startedAt),
    )[0];
    if (previous?.completedAt && quest.cooldownMinutes > 0) {
      const unlockAt =
        new Date(previous.completedAt).getTime() + quest.cooldownMinutes * 60_000;
      if (unlockAt > Date.now()) {
        throw new Error(
          `This quest reopens at ${new Date(unlockAt).toLocaleTimeString()}.`,
        );
      }
    }
    const attempt: Attempt = {
      id: crypto.randomUUID(),
      questId: quest.id,
      questVersion: quest.version,
      studentId: user.id,
      seed: createTimeSeed(),
      attemptNumber: questAttempts.length + 1,
      status: "active",
      startedAt: new Date().toISOString(),
      awardedXp: 0,
      awardedGradePoints: 0,
    };
    setStudentProgress((currentProgress) => ({
      ...currentProgress,
      [user.id]: {
        ...(currentProgress[user.id] ?? DEMO_PROGRESS),
        attempts: [
          ...(currentProgress[user.id]?.attempts ?? DEMO_PROGRESS.attempts),
          attempt,
        ],
      },
    }));
    return {
      attempt,
      instance: generatePuzzleInstance(quest.templateId, quest.config, attempt.seed),
    };
  };

  const submitAttempt = async (
    attempt: Attempt,
    instance: unknown,
    quest: Quest,
    submission: Record<string, unknown>,
    reflection: string,
  ): Promise<SubmitAttemptResult> => {
    if (!user) throw new Error("Your session has expired.");
    if (!demoMode && supabase) {
      const { data, error } = await supabase.functions.invoke("submit-attempt", {
        body: { attemptId: attempt.id, submission, reflection },
      });
      if (error) {
        throw new Error(
          await getFunctionErrorMessage(error, "Unable to submit puzzle attempt."),
        );
      }
      const completed = data as SubmitAttemptResult;
      const prior = studentProgress[user.id] ?? DEMO_PROGRESS;
      const completedQuestIds = completed.success
        ? [...new Set([...prior.completedQuestIds, quest.id])]
        : prior.completedQuestIds;
      const playableInRegion = curriculum.quests.filter(
        (candidate) =>
          candidate.regionId === quest.regionId &&
          candidate.templateId !== "coming-soon",
      );
      const regionCompletion = Math.round(
        (playableInRegion.filter((candidate) =>
          completedQuestIds.includes(candidate.id),
        ).length /
          Math.max(1, playableInRegion.length)) *
          100,
      );
      const evidence = { ...prior.competencyEvidence };
      if (completed.success && completed.attempt.awardedXp > 0) {
        Object.entries(quest.competencyEvidence).forEach(([id, points]) => {
          evidence[id] = Math.min(100, (evidence[id] ?? 0) + points);
        });
      }
      setStudentProgress((current) => ({
        ...current,
        [user.id]: {
          ...prior,
          completedQuestIds,
          competencyEvidence: evidence,
          regionCompletion: {
            ...prior.regionCompletion,
            [quest.regionId]: regionCompletion,
          },
          attempts: [
            ...prior.attempts.filter(
              (candidate) => candidate.id !== completed.attempt.id,
            ),
            completed.attempt,
          ],
        },
      }));
      if (completed.attempt.awardedXp > 0) {
        let xp = user.xp + completed.attempt.awardedXp;
        let level = user.level;
        let xpNext = user.xpNext;
        while (xp >= xpNext) {
          xp -= xpNext;
          level += 1;
          xpNext = Math.floor(xpNext * 1.35);
        }
        setUser({ ...user, xp, level, xpNext });
      }
      return completed;
    }

    const validation = validatePuzzleSubmission(
      quest.templateId,
      quest.config,
      instance,
      submission,
    );
    const priorProgress = studentProgress[user.id] ?? DEMO_PROGRESS;
    const alreadyRewarded = priorProgress.attempts.some(
      (candidate) =>
        candidate.questId === quest.id &&
        candidate.questVersion === quest.version &&
        candidate.status === "succeeded",
    );
    const awardedXp =
      validation.success && !alreadyRewarded
        ? calculateReward(quest.xp, quest.xpDecay, attempt.attemptNumber)
        : 0;
    const awardedGradePoints =
      validation.success && !alreadyRewarded
        ? calculateReward(
            quest.gradePoints,
            quest.gradeDecay,
            attempt.attemptNumber,
          )
        : 0;
    const completedAttempt: Attempt = {
      ...attempt,
      status: validation.success ? "succeeded" : "failed",
      completedAt: new Date().toISOString(),
      awardedXp,
      awardedGradePoints,
      submission,
      reflection,
      feedback: validation.feedback,
    };
    const nextCompleted = validation.success
      ? [...new Set([...priorProgress.completedQuestIds, quest.id])]
      : priorProgress.completedQuestIds;
    const playableInRegion = curriculum.quests.filter(
      (candidate) =>
        candidate.regionId === quest.regionId &&
        candidate.templateId !== "coming-soon" &&
        candidate.status === "published",
    );
    const completedInRegion = playableInRegion.filter((candidate) =>
      nextCompleted.includes(candidate.id),
    ).length;
    const regionCompletion = validation.success
      ? Math.round((completedInRegion / Math.max(1, playableInRegion.length)) * 100)
      : priorProgress.regionCompletion[quest.regionId] ?? 0;
    const evidence = { ...priorProgress.competencyEvidence };
    if (validation.success && !alreadyRewarded) {
      Object.entries(quest.competencyEvidence).forEach(([id, points]) => {
        evidence[id] = Math.min(100, (evidence[id] ?? 0) + points);
      });
    }
    const nextProgress: StudentProgress = {
      ...priorProgress,
      completedQuestIds: nextCompleted,
      attempts: priorProgress.attempts.map((candidate) =>
        candidate.id === attempt.id ? completedAttempt : candidate,
      ),
      regionCompletion: {
        ...priorProgress.regionCompletion,
        [quest.regionId]: regionCompletion,
      },
      competencyEvidence: evidence,
      badges:
        validation.success &&
        !priorProgress.badges.some((badge) => badge.id === "first-puzzle")
          ? [
              ...priorProgress.badges,
              {
                id: "first-puzzle",
                name: "Systems Solver",
                description: "Completed an interactive math system.",
                icon: "Sparkles",
                earnedAt: new Date().toISOString(),
              },
            ]
          : priorProgress.badges,
    };
    setStudentProgress((current) => ({ ...current, [user.id]: nextProgress }));
    if (awardedXp > 0) {
      let xp = user.xp + awardedXp;
      let level = user.level;
      let xpNext = user.xpNext;
      while (xp >= xpNext) {
        xp -= xpNext;
        level += 1;
        xpNext = Math.floor(xpNext * 1.35);
      }
      await updateProfile({ xp, level, xpNext });
      notify(
        validation.success ? "Quest mastered" : "Attempt recorded",
        `+${awardedXp} XP and ${awardedGradePoints} grade points`,
      );
    }
    return {
      attempt: completedAttempt,
      success: validation.success,
      feedback: validation.feedback,
      score: validation.score,
    };
  };

  const saveCurriculum = async (next: Curriculum) => {
    const draft = {
      ...next,
      status: "draft",
      updatedAt: new Date().toISOString(),
    } as Curriculum;
    setCurriculum(draft);
    if (!demoMode && supabase) {
      const { data, error } = await supabase.functions.invoke<{
        curriculum?: { id: string; version: number };
      }>("publish-curriculum", {
        body: { mode: "save", curriculum: draft },
      });
      if (error) {
        throw new Error(
          await getFunctionErrorMessage(error, "Unable to save curriculum."),
        );
      }
      if (data?.curriculum) {
        setCurriculum((current) => ({
          ...current,
          id: data.curriculum!.id,
          version: data.curriculum!.version,
        }));
      }
    }
    notify("Draft saved", "Your published student version is unchanged.", "info");
  };

  const publishCurriculum = async (next = curriculum) => {
    let publishedId = next.id;
    let publishedVersion = next.version + 1;
    if (!demoMode && supabase) {
      const { data, error } = await supabase.functions.invoke("publish-curriculum", {
        body: { mode: "publish", curriculum: next },
      });
      if (error) {
        throw new Error(
          await getFunctionErrorMessage(error, "Unable to publish curriculum."),
        );
      }
      publishedId = data.curriculum.id;
      publishedVersion = data.curriculum.version;
    }
    setCurriculum({
      ...next,
      id: publishedId,
      version: publishedVersion,
      status: "published",
      updatedAt: new Date().toISOString(),
      quests: next.quests.map((quest) => ({
        ...quest,
        status: "published",
        version: quest.version + 1,
      })),
    });
    notify(
      "Curriculum published",
      "New attempts will use the new immutable version.",
    );
  };

  const addRoster = async (
    entries: Array<{ email: string; fullName: string }>,
  ) => {
    if (!demoMode && supabase) {
      const { data, error } = await supabase.functions.invoke("invite-users", {
        body: { cohortId: cohort.id, users: entries },
      });
      if (error) {
        throw new Error(
          await getFunctionErrorMessage(error, "Unable to update the roster."),
        );
      }
      return Number(data?.invited ?? entries.length);
    }
    const existing = new Set(students.map((student) => student.email));
    const additions = entries
      .filter((entry) => !existing.has(entry.email))
      .map((entry) => ({
        ...DEMO_STUDENT,
        id: crypto.randomUUID(),
        fullName: entry.fullName || entry.email.split("@")[0],
        email: entry.email,
        level: 1,
        xp: 0,
        xpNext: 500,
        guild: "explorer" as const,
        guildReputation: {
          explorer: 0,
          creator: 0,
          designer: 0,
          ai: 0,
          challenger: 0,
        },
      }));
    setStudents((current) => [...current, ...additions]);
    setStudentProgress((current) => ({
      ...current,
      ...Object.fromEntries(
        additions.map((student) => [
          student.id,
          {
            ...DEMO_PROGRESS,
            completedQuestIds: [],
            attempts: [],
            badges: [],
            regionCompletion: Object.fromEntries(
              curriculum.regions.map((region) => [region.id, 0]),
            ),
            competencyEvidence: Object.fromEntries(
              curriculum.competencies.map((competency) => [competency.id, 0]),
            ),
          },
        ]),
      ),
    }));
    setCohort((current) => ({
      ...current,
      studentIds: [...current.studentIds, ...additions.map((student) => student.id)],
    }));
    notify("Roster updated", `${additions.length} invitation(s) prepared.`);
    return additions.length;
  };

  const updateRegionUnlocks = async (
    regionId: string,
    settings: Pick<
      Region,
      | "unlockAvailableFrom"
      | "unlockCompletionThreshold"
      | "unlockPrerequisiteRegionIds"
    >,
  ) => {
    const patch = {
      unlockAvailableFrom: settings.unlockAvailableFrom ?? null,
      unlockCompletionThreshold: settings.unlockCompletionThreshold ?? 70,
      unlockPrerequisiteRegionIds: settings.unlockPrerequisiteRegionIds ?? [],
    };
    if (!demoMode && supabase) {
      const { data, error } = await supabase.functions.invoke(
        "update-region-unlocks",
        {
          body: {
            cohortId: cohort.id,
            regionId,
            availableFrom: patch.unlockAvailableFrom,
            completionThreshold: patch.unlockCompletionThreshold,
            prerequisiteRegionIds: patch.unlockPrerequisiteRegionIds,
          },
        },
      );
      if (error) {
        throw new Error(
          await getFunctionErrorMessage(error, "Unable to update region unlocks."),
        );
      }
      Object.assign(patch, data ?? {});
    }
    setCurriculum((current) => ({
      ...current,
      regions: current.regions.map((region) =>
        region.id === regionId ? { ...region, ...patch } : region,
      ),
    }));
    notify("Unlock rules saved", "Cohort access has been updated.", "info");
  };

  const updateRiskRules = (rules: RiskRules) => {
    setCohort((current) => ({ ...current, riskRules: rules }));
    notify("Risk rules saved", "Analytics have been recalculated.", "info");
  };

  const value: PortalContextValue = {
    user,
    authLoading,
    isDemo: demoMode,
    curriculum,
    cohort,
    progress: user ? studentProgress[user.id] ?? DEMO_PROGRESS : DEMO_PROGRESS,
    students,
    studentProgress,
    toasts,
    signInWithGoogle,
    signInWithMagicLink,
    signInDemo,
    signOut,
    switchGuild,
    updateProfile,
    startAttempt,
    submitAttempt,
    saveCurriculum,
    publishCurriculum,
    addRoster,
    updateRegionUnlocks,
    updateRiskRules,
    notify,
    dismissToast: (id) =>
      setToasts((current) => current.filter((toast) => toast.id !== id)),
  };

  return (
    <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
  );
}

export function usePortal() {
  const context = useContext(PortalContext);
  if (!context) throw new Error("usePortal must be used inside PortalProvider.");
  return context;
}
